import {
  LoadingIndicatorMessage,
  NoUserActionMessage,
  UserActionRequiredMessage,
} from "@sgwt-widget/components-base";
import { Component, h } from "preact";
import { UserActionOnAuthErrorMessage } from "../generic/generic-message";
import { DataStatus } from "../generic/generic-types";
import { maxSelectedClients } from "../utils/clientlistHelper";
import { generateUniqueId } from "../utils/uniqueId";
import { RawUserMessage } from "./Messages/RawUserMessage";

interface IState {
  dataStatus: DataStatus;
  correlationId: string;
  statusText?: string;
}

/**
 * preact components that interact with the API to fetch data necessary for their display
 * should extend from this class.
 */
export abstract class ApiBackedComponent<P extends {}, S> extends Component<
  P,
  S & IState
> {
  public static CLIENT_ERROR = `The requested data cannot be loaded.
    Please either reduce the number of columns that you have chosen, or apply a filter on specific clients/business lines`;

  public render() {
    let requestIdMessage = "";
    if (this.state.correlationId !== undefined) {
      requestIdMessage = ` (request id: ${this.state.correlationId.substr(
        0,
        8
      )})`;
    }

    const noUserActionMapping: {
      [dataStatus: string]: string | JSX.Element;
    } = {
      "client-request-error":
        ApiBackedComponent.CLIENT_ERROR + requestIdMessage,
      "no-attribute-selected": "Please select at least one attribute",
      "no-client-mapping": (
        <span>
          This client is not in your iC portfolio.
          <br />
          Please go to{" "}
          <a href="https://ic.sgmarkets.com">https://ic.sgmarkets.com</a>,
          search for the client and click “Add to my portfolio”{" "}
          {requestIdMessage}
        </span>
      ),
      "no-data": `Profitability data has not been found for this selection ${requestIdMessage}`,
      "no-kpi-selected": "Please select at least one kpi",
      "no-portfolio-visibility": `This widget can only be used by users with PCRU role on the client (Bankers, RelationshipManagers etc..)${requestIdMessage}`,
    };
    if (noUserActionMapping[this.state.dataStatus] !== undefined) {
      const status = noUserActionMapping[this.state.dataStatus];
      return (
        <RawUserMessage>
          {status instanceof String ? (
            <span>{status}</span>
          ) : (
            (status as JSX.Element)
          )}
        </RawUserMessage>
      );
    }
    switch (this.state.dataStatus) {
      case "loading":
        return <LoadingIndicatorMessage />;
      case "ready":
        return this.renderWithDataReady();
      case "server-error":
        return (
          <NoUserActionMessage message={"Server error" + requestIdMessage} />
        );
      case "no-client":
        return <UserActionRequiredMessage message="Please select a client" />;
      case "abacus-validation":
        return (
          <UserActionRequiredMessage message="Please select client type" />
        );
      case "abacus-level-error":
        return (
          <UserActionRequiredMessage
            message={
              "No data available at this level, please select at least a Legal Entity" +
              requestIdMessage
            }
          />
        );
        case "incompatible-filters-error":
          return (
            <UserActionRequiredMessage
              message={
                `${this.state.statusText} has been defined in both standard and advanced filters, and this is causing a conflict. Please update.`
              }
            />
          );
      case "no-access-rights":
        return <UserActionOnAuthErrorMessage message={this.state.statusText} />;
      case "too-many-clients":
        return (
          <NoUserActionMessage
            message={`Number of selected clients must be lower than ${maxSelectedClients}. Please reduce the number of clients`}
          />
        );
    }
    return <div />;
  }

  public componentDidMount() {
    this.setDataStatus("no-client");
    this.fetchApiData(this.props);
    this.setState({});
  }
  public componentWillReceiveProps(nextProps: P) {
    if (this.shouldFetchApiData(nextProps)) {
      this.fetchApiData(nextProps);
    }
  }
  public shouldComponentUpdate(
    nextProps: P,
    nextState: Readonly<S & IState>,
    nextContext: any
  ): boolean {
    if (
      this.shouldFetchApiData(nextProps) ||
      this.state !== nextState ||
      this.context !== nextContext
    ) {
      return true;
    }
    return false;
  }

  /**
   * Return true if the nextProps indicates that the data should be fetched from the API again.
   * @param nextProps
   */
  protected shouldFetchApiData(nextProps: P): boolean {
    return true;
  }

  /**
   * Implement your data loading in this method. Make sure to call this.setDataStatus appropriately
   * during your processing, and call initCorrelationId() at the start of the function.
   */
  protected abstract fetchApiData(props: Readonly<P>): void | Promise<void>;

  /**
   * Render the content that should be shown when the data is available.
   */
  protected abstract renderWithDataReady(): JSX.Element;

  protected setDataStatus(status: DataStatus) {
    this.setState({ ...this.state, dataStatus: status });
  }
  protected setStatusText(statusText?: string) {
    this.setState({ ...this.state, statusText: statusText });
  }

  /**
   * Initializes a unique id in the state
   */
  protected initCorrelationId() {
    const correlationId = generateUniqueId();
    this.setState({ ...this.state, correlationId });
  }
}
